import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { GET_DELIVERY_PROFILE_QUERY, SET_DELIVERY_PROFILE_MUTATION } from './queries';
import { DeliveryProfile, QueryDeliveryProfileArgs, SetDeliveryProfileInput } from '@shopify-multiseller/graphql/partner/types';
import { DeliveryProfileEditor } from './DeliveryProfileEditor';
import { DeliveryProfileSelector } from './DeliveryProfileSelector';

export interface DeliveryProfileEditorParams {
  profileId?: string;
  onSave: (_profileId: string) => void;
}

interface DeliveryProfileSelectorParams {
  onSelect: (_profileId: string) => void;
}

interface DeliveryProfileContextType {
  openEditor: (_params: DeliveryProfileEditorParams) => void;
  openSelector: (_params: DeliveryProfileSelectorParams) => void;
  closeEditor: () => void;
  loading: boolean;
  error?: string;
  deliveryProfile?: DeliveryProfile;
  isEditorOpen: boolean;
  clear: () => void;
  setCurrentProfile: (_profileId: string) => void;
}

const DeliveryProfileContext = createContext<DeliveryProfileContextType | undefined>(undefined);

export const useDeliveryProfile = () => {
  const context = useContext(DeliveryProfileContext);
  if (!context) {
    throw new Error('useDeliveryProfile must be used within a DeliveryProfileProvider');
  }
  return context;
};

interface DeliveryProfileProviderProps {
  children: React.ReactNode;
}

export const DeliveryProfileProvider: React.FC<DeliveryProfileProviderProps> = ({ children }) => {
  const [showEditor, setShowEditor] = useState(false);
  const [showSelector, setShowSelector] = useState(false);
  const [currentProfileId, setCurrentProfileId] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [deliveryProfile, setDeliveryProfile] = useState<DeliveryProfile | undefined>();
  const [editorParams, setEditorParams] = useState<DeliveryProfileEditorParams | undefined>();
  const [editorKey, setEditorKey] = useState(0);
  const [previousProfileState, setPreviousProfileState] = useState<{
    profileId?: string;
    profile?: DeliveryProfile;
  } | undefined>();
  

  const [getDeliveryProfile] = useLazyQuery<
    { deliveryProfile: DeliveryProfile },
    QueryDeliveryProfileArgs
  >(GET_DELIVERY_PROFILE_QUERY);

  const [setDeliveryProfileMutation] = useMutation<
    { setDeliveryProfile: DeliveryProfile },
    { deliveryProfile: SetDeliveryProfileInput }
  >(SET_DELIVERY_PROFILE_MUTATION);

  const setCurrentProfile = (profileId: string) => {
    setCurrentProfileId(profileId);
  };

  useEffect(() => {
    if (currentProfileId) {
      setLoading(true);
      getDeliveryProfile({
        variables: {
          deliveryProfileId: currentProfileId
        },
        fetchPolicy: 'network-only'
      }).then((result) => {
        if (result.data?.deliveryProfile) {
          setDeliveryProfile(result.data.deliveryProfile);
        }
        setLoading(false);
      }).catch((err) => {
        setError(err.message);
        setLoading(false);
      });
    } else {
      setDeliveryProfile(undefined);
      setLoading(false);
    }
  }, [currentProfileId]);

  const openEditor = (params: DeliveryProfileEditorParams) => {
    if (!params.profileId && currentProfileId) {
      setPreviousProfileState({
        profileId: currentProfileId,
        profile: deliveryProfile
      });
    }
    setCurrentProfileId(params.profileId);
    setEditorParams(params);
    setShowEditor(true);
    setError(undefined);
    setEditorKey(key => key + 1);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setError(undefined);
    setEditorParams(undefined);
    setLoading(false);
    if (previousProfileState) {
      setCurrentProfileId(previousProfileState.profileId);
      setDeliveryProfile(previousProfileState.profile);
      setPreviousProfileState(undefined);
    }
  };

  const openSelector = (params: DeliveryProfileSelectorParams) => {
    setShowSelector(true);
    setError(undefined);
    setEditorParams({ onSave: params.onSelect });
  };

  const handleCreate = async (profile: SetDeliveryProfileInput) => {
    try {
      setLoading(true);
      const result = await setDeliveryProfileMutation({
        variables: {
          deliveryProfile: profile
        }
      });

      if (result.data?.setDeliveryProfile) {
        const newProfileId = result.data.setDeliveryProfile.id;
        setDeliveryProfile(result.data.setDeliveryProfile);
        setCurrentProfileId(newProfileId);
        editorParams?.onSave(newProfileId);
        setPreviousProfileState(undefined);
        setShowEditor(false);        
      }
 
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (profile: SetDeliveryProfileInput) => {
    try {
      setLoading(true);
      const result = await setDeliveryProfileMutation({
        variables: {
          deliveryProfile: profile
        }
      });
      if (result.data?.setDeliveryProfile) {
        const updatedProfileId = result.data.setDeliveryProfile.id;
        setDeliveryProfile(result.data.setDeliveryProfile);
        editorParams?.onSave(updatedProfileId);
      }
      closeEditor();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (id: string) => {
      setLoading(true);
      getDeliveryProfile({
        variables: {
          deliveryProfileId: id
        }
      }).then((result) => {
        if (result.data?.deliveryProfile) {
          setDeliveryProfile(result.data.deliveryProfile);
          setCurrentProfileId(id);
          if (editorParams?.onSave) {
            editorParams.onSave(id);
          }
        }
        setLoading(false);
      }).catch((err) => {
        setError(err.message);
        setLoading(false);
      });
      setShowSelector(false);
  }

  const clear = () => {
    setCurrentProfileId(undefined);
    setDeliveryProfile(undefined);
  };

  return (
    <DeliveryProfileContext.Provider value={{
      openEditor,
      closeEditor,
      loading,
      error,
      deliveryProfile,
      isEditorOpen: showEditor,
      openSelector,
      clear,
      setCurrentProfile,
    }}>
      {children}

      <DeliveryProfileEditor
        key={editorKey}
        open={showEditor}
        deliveryProfileId={currentProfileId}
        onClose={closeEditor}
        error={error}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
      />

      <DeliveryProfileSelector
        open={showSelector}
        onEdit={(id) => {
          setCurrentProfileId(id);
          setShowSelector(false);
          openEditor({ profileId: id, onSave: editorParams?.onSave || (() => {}) });
        }}
        onClose={() => setShowSelector(false)}
        selectedProfileId={currentProfileId}
        onSelect={handleSelect}
        onAdd={() => {
          setShowSelector(false);
          openEditor({  onSave: editorParams?.onSave || (() => {}) });
        }}
      />
    </DeliveryProfileContext.Provider>
  );
}; 


